import React from "react";
import MerchantRow, { MerchantRecord } from "components/merchant-row/MerchantRow";
import MerchantRowSkeleton from "components/merchant-row/MerchantRowSkeleton";
import Button from "components/button/Button";
import SearchInput from "components/search-input/SearchInput";
import { useSearch } from "components/search-input/useSearch";
import { useDebouncedValue } from "hooks/useDebouncedValue";
import { useMerchantLookup } from "features/merchants/useMerchantLookup";

const MERCHANT_SEARCH_KEYS: (keyof MerchantRecord)[] = [
  "merchantId",
  "merchantName",
];
// merchantId is ver|MCC(4)|country(3)|seq(6) composed as an UNPADDED number, so a complete
// id runs from 12 digits (version 0 with one of the three 3-digit MCCs — 742/763/780) up to
// 15 (version >= 10). The upper bound matters as much as the lower: merchantAccount takes a
// uint64, so an over-long paste throws in the ABI encoder and comes back as a retryable error
// instead of a miss.
const MIN_MERCHANT_ID_LENGTH = 12;
const MAX_MERCHANT_ID_LENGTH = 15;
const isFullMerchantId = (value: string) =>
  /^\d+$/.test(value) &&
  value.length >= MIN_MERCHANT_ID_LENGTH &&
  value.length <= MAX_MERCHANT_ID_LENGTH;

// The chain read resolves through BigInt, which drops leading zeros, but the signed-up flag is
// an exact-match Firestore read on the id as sent — so a zero-padded spelling has to be
// canonicalised here or the row renders with the wrong signup state.
const canonicalMerchantId = (value: string) =>
  isFullMerchantId(value) ? String(BigInt(value)) : value;

interface Props {
  merchants: MerchantRecord[];
  activeMerchants: number;
  loading: boolean;
  onAddMerchant: () => void;
  onSignUp: (merchantId: string) => void;
}

function MerchantsList({
  merchants,
  activeMerchants,
  loading,
  onAddMerchant,
  onSignUp,
}: Props) {
  const { query, setQuery, results } = useSearch(
    merchants,
    MERCHANT_SEARCH_KEYS,
  );

  console.log(`MerchantsList => merchants: `, merchants);

  const trimmedQuery = query.trim();

  // Only the recent merchants are loaded up front; a search miss here is our cue to
  // check the chain — but only once a complete merchant id has been entered.
  const noLocalMatch =
    !loading && trimmedQuery.length > 0 && results.length === 0;

  // Hold the network lookup until typing settles, so we don't fire a request
  // (and flash "not found") on every keystroke.
  const debouncedQuery = useDebouncedValue(trimmedQuery, 400);
  const settling = debouncedQuery !== trimmedQuery;
  const lookup = useMerchantLookup(
    canonicalMerchantId(debouncedQuery),
    noLocalMatch && isFullMerchantId(trimmedQuery) && !settling,
  );

  // While the debounce catches up we're still effectively searching; once it
  // settles, the hook's status drives the outcome (loading/found/miss/error).
  const chainStatus = settling ? "loading" : lookup.status;

  return (
    <>
      <div className="ip-section-header">
        <div className="ip-section-title-group">
          <h3 className="ip-section-title">Merchants</h3>
          {/* <span className="ip-section-meta">{activeMerchants} active</span> */}
        </div>
        <div className="ip-section-actions">
          <SearchInput
            value={query}
            onChange={setQuery}
            placeholder="Search by merchant ID or name..."
          />
          <Button
            variant="primary"
            size="sm"
            className="ip-section-cta"
            onClick={onAddMerchant}
          >
            + Add merchant
          </Button>
        </div>
      </div>

      <div className="ip-merch-list">
        {loading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <MerchantRowSkeleton key={i} />
          ))
        ) : results.length > 0 ? (
          results.map((m) => (
            <MerchantRow key={m.merchantId} {...m} onSignUp={onSignUp} />
          ))
        ) : trimmedQuery.length === 0 ? (
          // An empty list isn't a search miss — say so, the way CardsList does.
          <p className="ip-empty">No merchants yet.</p>
        ) : !isFullMerchantId(trimmedQuery) ? (
          <p className="ip-empty">
            No matching merchant. Enter the full merchant ID to look it up
            on-chain.
          </p>
        ) : chainStatus === "loading" ? (
          <MerchantRowSkeleton />
        ) : chainStatus === "found" && lookup.merchant ? (
          <>
            <p className="ip-lookup-note">
              Not in recent merchants — found on-chain.
            </p>
            <MerchantRow {...lookup.merchant} onSignUp={onSignUp} />
          </>
        ) : chainStatus === "not-found" ? (
          <p className="ip-empty">No merchant found for “{trimmedQuery}”.</p>
        ) : chainStatus === "error" ? (
          <div className="ip-lookup-error">
            <p className="ip-lookup-error__msg">
              Couldn’t look up this merchant right now.
            </p>
            <Button variant="ghost" size="sm" onClick={() => lookup.refetch()}>
              Retry
            </Button>
          </div>
        ) : (
          // `idle` — the gate closed under us (e.g. auth dropped). Say nothing rather
          // than claim a lookup failed that never ran.
          <p className="ip-empty">No merchants match your search.</p>
        )}
      </div>
    </>
  );
}

export default MerchantsList;
